/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';

type TextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type TextTone = 'primary' | 'secondary' | 'error' | 'warning' | 'overlay' | 'accent' | 'inherit';
type TextAlign = 'left' | 'center' | 'right';
type TextComponent = 'span' | 'p' | 'div';
type TextLineHeight = 'normal' | 'relaxed';

interface TextProps {
    children: React.ReactNode;
    /** Visual size: `xs` (11px, for tiny captions/badges/metadata), `sm` (13px), `md` (14px, default), or `xl` (24px, for standout numbers/figures). */
    size?: TextSize;
    /** Font weight: `regular` (400, default), `medium` (500), `semibold` (600), or `bold` (700). */
    weight?: TextWeight;
    /** Color tone: `primary` (darkNavy, default), `secondary` (mediumGrey), `error` (mediumRed), `warning` (warning amber/orange for partial/incomplete states), `overlay` (white, for text on colored backgrounds), `accent` (turquoise), or `inherit` (inherits color from parent, for cases like message bubbles where the parent controls text color). */
    tone?: TextTone;
    /** Text alignment within the element. */
    align?: TextAlign;
    /**
     * HTML element to render.
     * - `span` (default): inline text within a sentence or content flow.
     * - `p`: standalone paragraph or body text — carries semantic paragraph meaning.
     * - `div`: block-level text that isn't a paragraph (labels, values, table cells, layout blocks).
     */
    component?: TextComponent;
    /** Line height: `normal` (inherited, default) or `relaxed` (1.5, for multi-line body text). */
    lineHeight?: TextLineHeight;
    /** If true, clips overflowing text to a single line with an ellipsis. */
    truncate?: boolean;
    /** If true, preserves whitespace and line breaks in the text content (for user-generated content). */
    preserveWhitespace?: boolean;
    /** If true, renders the text in italics (e.g. loading placeholders, tentative copy). */
    italic?: boolean;
    /** If true, renders the text uppercase with a small letter-spacing — for section/field labels. */
    uppercase?: boolean;
    className?: string;
    lineClamp?: number;
}

const SIZE_PX: Record<TextSize, number> = { xs: 11, sm: 13, md: 14, lg: 18, xl: 24 };
const WEIGHT_VALUE: Record<TextWeight, number> = { regular: 400, medium: 500, semibold: 600, bold: 700 };
const TONE_COLOR: Record<TextTone, string> = {
    primary: Colors.primary,
    secondary: Colors.textSecondary,
    error: Colors.error,
    warning: Colors.warning,
    overlay: Colors.white,
    accent: Colors.secondary,
    inherit: 'inherit',
};
const LINE_HEIGHT_VALUE: Record<TextLineHeight, number | undefined> = { normal: undefined, relaxed: 1.5 };

type TextStyleProps = {
    $size: TextSize;
    $weight: TextWeight;
    $tone: TextTone;
    $align?: TextAlign;
    $lineHeight: TextLineHeight;
    $truncate: boolean;
    $preserveWhitespace: boolean;
    $italic: boolean;
    $uppercase: boolean;
    $lineClamp?: number;
};

const textStyles = ({
    $size,
    $weight,
    $tone,
    $align,
    $lineHeight,
    $truncate,
    $preserveWhitespace,
    $italic,
    $uppercase,
    $lineClamp,
}: TextStyleProps) => ({
    fontSize: SIZE_PX[$size],
    fontWeight: WEIGHT_VALUE[$weight],
    color: TONE_COLOR[$tone],
    textAlign: $align,
    lineHeight: LINE_HEIGHT_VALUE[$lineHeight],
    fontStyle: $italic ? ('italic' as const) : undefined,
    margin: 0,
    ...($truncate && {
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        whiteSpace: 'nowrap' as const,
    }),
    ...($preserveWhitespace && !$truncate && { whiteSpace: 'pre-wrap' as const }),
    ...($uppercase && { textTransform: 'uppercase' as const, letterSpacing: 0.5 }),
    ...($lineClamp && {
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        display: '-webkit-box' as const,
        WebkitLineClamp: $lineClamp,
        WebkitBoxOrient: 'vertical' as const,
    }),
});

const shouldForwardTextProp = (prop: string) =>
    ![
        '$size',
        '$weight',
        '$tone',
        '$align',
        '$lineHeight',
        '$truncate',
        '$preserveWhitespace',
        '$italic',
        '$uppercase',
    ].includes(prop);

const SpanText = styled('span', { shouldForwardProp: (p) => shouldForwardTextProp(p as string) })<TextStyleProps>(
    textStyles,
);
const PText = styled('p', { shouldForwardProp: (p) => shouldForwardTextProp(p as string) })<TextStyleProps>(textStyles);
const DivText = styled('div', { shouldForwardProp: (p) => shouldForwardTextProp(p as string) })<TextStyleProps>(
    textStyles,
);

const COMPONENTS = { span: SpanText, p: PText, div: DivText };

export function Text({
    children,
    size = 'md',
    weight = 'regular',
    tone = 'primary',
    align,
    component = 'span',
    lineHeight = 'normal',
    truncate = false,
    preserveWhitespace = false,
    italic = false,
    uppercase = false,
    lineClamp,
    className,
}: TextProps) {
    const Component = COMPONENTS[component];
    return (
        <Component
            $size={size}
            $weight={weight}
            $tone={tone}
            $align={align}
            $lineHeight={lineHeight}
            $truncate={truncate}
            $preserveWhitespace={preserveWhitespace}
            $italic={italic}
            $uppercase={uppercase}
            $lineClamp={lineClamp}
            className={className}
        >
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
